import axios from "axios";
import { apiOrigin, apiUrl, buildLoginUrl } from "./apiClient";

/**
 * API client for the POC claim, warranty-policy and authorization-rule
 * screens (Phase 4B-UI). Every call goes through request() below, which:
 *
 * - sends the existing Entra ID session cookie (withCredentials), against the
 *   same config.API_BASE_URL as the rest of the app;
 * - on 401 sends the browser to sign-in, exactly like the global fetch
 *   interceptor does;
 * - on 403 does NOT redirect to /forbidden (the global interceptor's
 *   behaviour for fetch): an ICMS user who opens an administrator action
 *   should see an "access denied" message in place, not lose the page;
 * - turns every failure into an ApiError with a fixed, user-safe message.
 *   Server text is only passed through for 409/422, whose messages the
 *   backend composes itself; nothing else (SQL, paths) can reach the screen.
 *
 * Responses are never logged, and nothing is written to browser storage.
 */

const TIMEOUT_MS = 20000;

export const MESSAGES = {
  unavailable: "POC claim services are currently unavailable. The configured policy database may not be ready.",
  forbidden: "You do not have access to this action. Administrator access is required.",
  unauthorized: "Your session has expired. Redirecting to sign-in…",
  not_found: "The requested record was not found.",
  timeout: "The server did not respond in time. Your entries are kept; you can try again.",
  network: "The server could not be reached. Your entries are kept; you can try again.",
  server: "The server could not complete the request. Your entries are kept; you can try again.",
  malformed: "The server returned an unexpected response.",
};

export class ApiError extends Error {
  constructor(kind, { status = null, message = MESSAGES[kind] || MESSAGES.server, issues = [], fieldErrors = {} } = {}) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.issues = issues;            // policy validation issues: [{code, severity, field, message}]
    this.fieldErrors = fieldErrors;  // request validation errors: {field: message}
  }
}

export const isCancelled = (error) => error instanceof ApiError && error.kind === "cancelled";

function validationDetails(detail) {
  // FastAPI request validation: [{loc: ["body", "field"], msg}]; policy validation: {message, issues: [...]}
  if (Array.isArray(detail)) {
    const fieldErrors = {};
    for (const item of detail) {
      const loc = Array.isArray(item?.loc) ? item.loc : [];
      const field = String(loc[loc.length - 1] ?? "request");
      fieldErrors[field] = typeof item?.msg === "string" ? item.msg : "Invalid value.";
    }
    return { message: "Some values were not accepted by the server.", fieldErrors };
  }
  if (detail && typeof detail === "object") {
    const issues = Array.isArray(detail.issues) ? detail.issues : [];
    return { message: typeof detail.message === "string" ? detail.message : "The request was not accepted.", issues };
  }
  return { message: typeof detail === "string" ? detail : "The request was not accepted." };
}

export function toApiError(error) {
  if (error instanceof ApiError) return error;
  if (axios.isCancel(error) || error?.name === "CanceledError") return new ApiError("cancelled");
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") return new ApiError("timeout");
  const response = error?.response;
  if (!response) return new ApiError("network");
  const { status } = response;
  const detail = response.data?.detail;
  if (status === 401) return new ApiError("unauthorized", { status });
  if (status === 403) return new ApiError("forbidden", { status });
  if (status === 404) return new ApiError("not_found", { status });
  if (status === 409) return new ApiError("conflict", { status, message: typeof detail === "string" ? detail : "The request conflicts with the current record state." });
  if (status === 422) return new ApiError("validation", { status, ...validationDetails(detail) });
  if (status === 503) return new ApiError("unavailable", { status });
  return new ApiError("server", { status });
}

export async function request(method, path, { params, data, headers, signal } = {}) {
  try {
    const response = await axios.request({
      method,
      url: apiUrl(path),
      params,
      data,
      headers,
      signal,
      timeout: TIMEOUT_MS,
      withCredentials: true,
    });
    return { status: response.status, data: response.data };
  } catch (error) {
    const apiError = toApiError(error);
    if (apiError.kind === "unauthorized") {
      window.location.href = buildLoginUrl(apiOrigin());
    }
    throw apiError;
  }
}

const get = async (path, options) => (await request("get", path, options)).data;

/** Rejects a 2xx body that lacks the fields a screen depends on (partial or malformed response). */
export function expectShape(body, fields) {
  if (!body || typeof body !== "object" || fields.some((f) => !(f in body))) {
    throw new ApiError("malformed");
  }
  return body;
}

// --- idempotency -------------------------------------------------------------------

export function newIdempotencyKey() {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c?.randomUUID) return `ui-${c.randomUUID()}`;
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return `ui-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
  }
  return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const keyed = (key) => (key ? { "Idempotency-Key": key } : undefined);

// --- reference data and access ---------------------------------------------------

export const getAccess = (signal) => get("/poc-reference/access", { signal }).then((b) => expectShape(b, ["is_admin"]));
export const listManufacturers = (signal) =>
  get("/poc-reference/manufacturers", { signal }).then((b) => expectShape(b, ["items"]).items);
export const listModels = (manufacturerCode, signal) =>
  get("/poc-reference/models", { params: { manufacturer_code: manufacturerCode }, signal }).then((b) => expectShape(b, ["items"]).items);

// --- POC claims -----------------------------------------------------------------------

export const getClaimSummary = (signal) =>
  get("/poc-claims/summary", { signal }).then((b) => expectShape(b, ["total", "by_status", "by_manufacturer", "recent"]));

export const listClaims = ({ manufacturerCode, modelCode, claimStatus, limit, offset }, signal) =>
  get("/poc-claims", {
    params: {
      manufacturer_code: manufacturerCode || undefined, model_code: modelCode || undefined,
      claim_status: claimStatus || undefined, limit, offset,
    },
    signal,
  }).then((b) => expectShape(b, ["items", "total", "limit", "offset"]));

export const getClaim = (claimId, signal) =>
  get(`/poc-claims/${encodeURIComponent(claimId)}`, { signal }).then((b) => expectShape(b, ["claim_id", "claim_status"]));

export const getClaimHistory = (claimId, signal) =>
  get(`/poc-claims/${encodeURIComponent(claimId)}/history`, { signal }).then((b) => expectShape(b, ["claim_id", "events"]));

/** Returns {status, claim}: 201 for a new claim, 200 when the key and body replay an earlier submission. */
export async function submitClaim(payload, idempotencyKey) {
  const { status, data } = await request("post", "/poc-claims", { data: payload, headers: keyed(idempotencyKey) });
  return { status, claim: expectShape(data, ["claim_id", "claim_status"]) };
}

export async function reevaluateClaim(claimId, idempotencyKey) {
  const { data } = await request("post", `/poc-claims/${encodeURIComponent(claimId)}/reevaluate`, { headers: keyed(idempotencyKey) });
  return expectShape(data, ["claim_id", "claim_status"]);
}

// --- warranty policies and authorization rules (administrators) ---------------------------

const RECORDS = {
  policy: { base: "/policies", one: "policy" },
  rule: { base: "/authorization-rules", one: "rule" },
};

export const listRecords = (kind, { status, limit = 100, offset = 0 } = {}, signal) =>
  get(RECORDS[kind].base, { params: { status: status || undefined, limit, offset }, signal })
    .then((b) => expectShape(b, ["items", "total"]));

export const getRecord = (kind, id, signal) => get(`${RECORDS[kind].base}/${encodeURIComponent(id)}`, { signal });

export const createRecord = async (kind, body) => (await request("post", RECORDS[kind].base, { data: body })).data;

export const updateRecord = async (kind, id, body) =>
  (await request("put", `${RECORDS[kind].base}/${encodeURIComponent(id)}`, { data: body })).data;

export const validateRecord = async (kind, id) =>
  expectShape((await request("post", `${RECORDS[kind].base}/${encodeURIComponent(id)}/validate`)).data, ["issues", "activation"]);

/** action: "activate" | "retire". Returns {changed, record}. */
export async function transitionRecord(kind, id, action) {
  const { data } = await request("post", `${RECORDS[kind].base}/${encodeURIComponent(id)}/${action}`);
  expectShape(data, ["changed", RECORDS[kind].one]);
  return { changed: data.changed, record: data[RECORDS[kind].one] };
}

export const getLifecycle = (kind, id, signal) =>
  get(`${RECORDS[kind].base}/${encodeURIComponent(id)}/lifecycle`, { signal }).then((b) => {
    if (!Array.isArray(b)) throw new ApiError("malformed");
    return b;
  });
